import { DateTime } from "luxon";

import { DropOffSlot, IDropOffLeadRow } from "../../models/DropOffPlanner";
import printHtml from "../../utils/printHtml";

export interface DropOffPrintDay {
  date: string;
  leads: IDropOffLeadRow[];
}

const esc = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const sourceLabel = (lead: IDropOffLeadRow): string =>
  lead.source && lead.source.trim() ? lead.source.trim() : "Manual";

const makeModel = (lead: IDropOffLeadRow): string =>
  [lead.combinedMake, lead.combinedModel].filter(Boolean).join(" ") || "—";

const shiftLabel = (lead: IDropOffLeadRow): string =>
  lead.scheduledCollectionSlot === DropOffSlot.PM ? "PM" : "AM";

const sourceColor = (label: string): string => {
  const s = label.toLowerCase();
  if (s === "manual") return "#b45309";
  if (s.includes("iaa")) return "#c2410c";
  return "#0f766e";
};

const longDate = (iso: string): string => {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat("cccc d LLLL yyyy") : iso;
};

const sourceBreakdown = (leads: IDropOffLeadRow[]): string => {
  const counts = new Map<string, number>();
  leads.forEach((l) => {
    const label = sourceLabel(l);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  });

  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(
      ([label, count]) =>
        `<span class="src" style="color:${sourceColor(label)}">${esc(label)}: ${count}</span>`
    )
    .join('<span class="sep"> • </span>');
};

const dayTable = (leads: IDropOffLeadRow[]): string => {
  if (leads.length === 0) {
    return '<p class="empty">No drop offs scheduled.</p>';
  }

  const ordered = [...leads].sort((a, b) => shiftLabel(a).localeCompare(shiftLabel(b)));

  const rows = ordered
    .map(
      (l) => `
        <tr>
          <td class="vrm">${esc(l.vrm || "—")}</td>
          <td class="make">${esc(makeModel(l))}</td>
          <td>${esc(l.postcode || "—")}</td>
          <td style="color:${sourceColor(sourceLabel(l))}">${esc(sourceLabel(l))}</td>
          <td>${esc(shiftLabel(l))}</td>
        </tr>`
    )
    .join("");

  return `
    <table>
      <thead>
        <tr>
          <th>VRM</th>
          <th>MAKE / MODEL</th>
          <th>POSTCODE</th>
          <th>SOURCE</th>
          <th>SHIFT</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;
};

const daySection = (day: DropOffPrintDay): string => `
  <section class="day">
    <h2>${esc(longDate(day.date))} <span class="count">(${day.leads.length})</span></h2>
    ${day.leads.length > 0 ? `<div class="breakdown">${sourceBreakdown(day.leads)}</div>` : ""}
    ${dayTable(day.leads)}
  </section>`;

export default function printDropOffSchedule(days: DropOffPrintDay[]) {
  if (days.length === 0) return;

  const subtitle =
    days.length === 1
      ? longDate(days[0].date)
      : `${longDate(days[0].date)} — ${longDate(days[days.length - 1].date)}`;

  const html = `
    <html>
      <head>
        <title>Drop Off Schedule</title>
        <style>
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body {
            font-family: Arial, Helvetica, sans-serif;
            color: #111827;
            margin: 24px;
          }
          h1 {
            font-size: 22px;
            font-weight: 700;
            color: #1e293b;
            margin: 0;
          }
          .subtitle {
            font-size: 13px;
            color: #6b7280;
            margin: 2px 0 20px;
          }
          .day {
            page-break-after: always;
          }
          .day:last-child {
            page-break-after: auto;
          }
          h2 {
            font-size: 16px;
            font-weight: 700;
            margin: 0 0 2px;
          }
          h2 .count {
            font-weight: 400;
            color: #9ca3af;
          }
          .breakdown {
            font-size: 12px;
            color: #6b7280;
            margin-bottom: 8px;
          }
          .breakdown .sep {
            color: #9ca3af;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }
          th, td {
            border: 1px solid #d1d5db;
            padding: 5px 8px;
            text-align: left;
          }
          th {
            background-color: #f3f4f6;
            font-size: 11px;
            letter-spacing: 0.03em;
            color: #374151;
          }
          tr {
            page-break-inside: avoid;
          }
          td.vrm {
            font-weight: 700;
          }
          td.make {
            color: #1d4ed8;
          }
          .empty {
            font-size: 12px;
            color: #6b7280;
          }
        </style>
      </head>
      <body>
        <h1>Drop Off Schedule</h1>
        <div class="subtitle">${esc(subtitle)}</div>
        ${days.map(daySection).join("")}
      </body>
    </html>`;

  printHtml(html);
}
